import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Announcement,
  FiberNew,
  Update,
  Warning,
  Gavel,
  Info,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'feature' | 'update' | 'warning' | 'terms';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  published: boolean;
  expires_at?: string;
  extra_data?: {
    version?: string;
    link?: string;
  };
  creator?: {
    name?: string;
    email?: string;
  };
}

const PlatformNewsManagementPage: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [newsToDelete, setNewsToDelete] = useState<NewsItem | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info' as NewsItem['type'],
    priority: 'normal' as NewsItem['priority'],
    published: true,
    expires_at: null as Date | null,
    version: '',
    link: '',
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform-news', {
        params: { limit: 50, include_expired: true }
      });
      setNews(response.data);
    } catch (err) {
      console.error('Failed to fetch platform news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem?: NewsItem) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem.title,
        content: newsItem.content,
        type: newsItem.type,
        priority: newsItem.priority,
        published: newsItem.published,
        expires_at: newsItem.expires_at ? new Date(newsItem.expires_at) : null,
        version: newsItem.extra_data?.version || '',
        link: newsItem.extra_data?.link || '',
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        content: '',
        type: 'info',
        priority: 'normal',
        published: true,
        expires_at: null,
        version: '',
        link: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingNews(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        type: formData.type,
        priority: formData.priority,
        published: formData.published,
        expires_at: formData.expires_at?.toISOString(),
        extra_data: {
          version: formData.version || undefined,
          link: formData.link || undefined,
        },
      };

      if (editingNews) {
        await api.put(`/platform-news/${editingNews.id}`, payload);
      } else {
        await api.post('/platform-news', payload);
      }
      
      handleCloseDialog();
      fetchNews();
    } catch (err) {
      console.error('Failed to save news:', err);
      setError('Failed to save news');
    }
  };

  const handleDelete = async () => {
    if (!newsToDelete) return;
    
    try {
      await api.delete(`/platform-news/${newsToDelete.id}`);
      setDeleteDialogOpen(false);
      setNewsToDelete(null);
      fetchNews();
    } catch (err) {
      console.error('Failed to delete news:', err);
      setError('Failed to delete news');
    }
  };

  const handleTogglePublished = async (newsItem: NewsItem) => {
    try {
      await api.put(`/platform-news/${newsItem.id}`, {
        published: !newsItem.published,
      });
      fetchNews();
    } catch (err) {
      console.error('Failed to toggle published status:', err);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <FiberNew color="primary" fontSize="small" />;
      case 'update':
        return <Update color="info" fontSize="small" />;
      case 'warning':
        return <Warning color="warning" fontSize="small" />;
      case 'terms':
        return <Gavel color="error" fontSize="small" />;
      default:
        return <Info color="action" fontSize="small" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Announcement color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Platform News Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Create News
            </Button>
            <IconButton onClick={fetchNews}>
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {news.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {getIcon(item.type)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {item.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.priority}
                      size="small"
                      color={getPriorityColor(item.priority) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={item.published}
                        onChange={() => handleTogglePublished(item)}
                        size="small"
                      />
                      <Typography variant="caption">
                        {item.published ? 'Published' : 'Draft'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {format(new Date(item.created_at), 'MMM dd, yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.expires_at ? (
                      <Typography variant="caption" color={
                        new Date(item.expires_at) < new Date() ? 'error' : 'text.secondary'
                      }>
                        {format(new Date(item.expires_at), 'MMM dd, yyyy')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setNewsToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingNews ? 'Edit News' : 'Create News'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                fullWidth
                multiline
                rows={4}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as NewsItem['type'] })}
                    label="Type"
                  >
                    <MenuItem value="info">Information</MenuItem>
                    <MenuItem value="feature">New Feature</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="terms">Terms Update</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as NewsItem['priority'] })}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Version (optional)"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  fullWidth
                  placeholder="e.g., 2.1.0"
                />
                
                <TextField
                  label="Link (optional)"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  fullWidth
                  placeholder="https://..."
                />
              </Box>
              
              <DateTimePicker
                label="Expires At (optional)"
                value={formData.expires_at}
                onChange={(value: any) => setFormData({ ...formData, expires_at: value instanceof Date ? value : value?.toDate ? value.toDate() : null })}
                slotProps={{ textField: { fullWidth: true } }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                }
                label="Published"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.title || !formData.content}
            >
              {editingNews ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{newsToDelete?.title}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default PlatformNewsManagementPage;